import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const features = [
  { name: "CRM", desc: "CRM helps manage leads, customers, pipelines, and interactions." },
  { name: "ERP", desc: "ERP integrates finance, HR, inventory, and operations." },
  { name: "Manufacturing", desc: "Production planning, BOM, workflows, and raw materials." },
  { name: "Sales Management", desc: "Leads, quotations, orders, and revenue tracking." },
  { name: "Payroll Management", desc: "Salary, payslips, deductions, compliance automation." },
  { name: "Services Management", desc: "Customer tickets, services, and support workflow." },
  { name: "ACM Management", desc: "Asset & contract lifecycle management." },
  { name: "Inventory Management", desc: "Stock, warehouses, suppliers, movements." },
  { name: "Accounting", desc: "Invoices, expenses, GST, ledgers, reports." },
];

const intro =
  "Hi, I'm Agentforce, an AI agent that can answer your Salesforce questions and connect you with our Sales Experts. This experience, powered entirely by Agentforce, keeps improving daily! Ask me things like, 'Can I chat with a Sales Expert?' or 'What is CRM?'";

const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(false);
  const [typed, setTyped] = useState("");
  const [i, setI] = useState(0);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const inputRef = useRef(null);

  // typing animation
  useEffect(() => {
    if (open && i < intro.length) {
      const t = setTimeout(() => {
        setTyped((p) => p + intro[i]);
        setI((prev) => prev + 1);
      }, 25);
      return () => clearTimeout(t);
    }
  }, [i, open]);

  const sendMessage = () => {
    if (!inputRef.current.value.trim()) return;
    setMessages([...messages, { from: "user", text: inputRef.current.value }]);
    inputRef.current.value = "";
  };

  return (
    <>
      {/* FLOATING CHAT ICON */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        onClick={() => setOpen(!open)}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50
                   bg-gradient-to-r from-blue-600 to-indigo-600
                   text-white w-14 h-14 rounded-full shadow-xl text-xl"
      >
        💬
      </motion.button>

      {/* CHAT WINDOW */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 60 }}
            className={`
              fixed z-50 overflow-hidden flex flex-col shadow-2xl
              bottom-0 right-0
              w-full h-[92vh]
              sm:bottom-24 sm:right-6 sm:w-96 sm:h-[520px]
              ${dark ? "bg-gray-900 text-white" : "bg-white text-gray-900"}
              sm:rounded-xl
            `}
          >
            {/* HEADER */}
            <div className="flex justify-between items-center px-4 py-3
                            bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <span className="font-semibold">Agentforce</span>
              <div className="flex gap-3 items-center">
                <button onClick={() => setDark(!dark)}>
                  {dark ? "🌞" : "🌙"}
                </button>
                <button onClick={() => setOpen(false)}>✖</button>
              </div>
            </div>

            {/* BODY */}
            <div className="flex-1 p-4 space-y-3 overflow-y-auto text-sm">

              {/* BOT INTRO */}
              <div className={`${dark ? "bg-gray-800" : "bg-gray-100"} p-3 rounded-lg`}>
                {typed}
                {i < intro.length && <span className="animate-pulse">|</span>}
              </div>

              {/* USER MESSAGES */}
              {messages.map((m, idx) => (
                <div key={idx} className="text-right">
                  <span className="inline-block bg-blue-600 text-white px-3 py-2 rounded-lg max-w-[85%] break-words">
                    {m.text}
                  </span>
                </div>
              ))}

              {/* OPTIONS */}
              {!selected && i === intro.length && (
                <div className="grid grid-cols-2 gap-2">
                  {features.map((f) => (
                    <motion.button
                      key={f.name}
                      whileHover={{ scale: 1.05 }}
                      onClick={() => setSelected(f)}
                      className="px-3 py-2 rounded-lg border text-xs sm:text-sm
                                 hover:bg-blue-600 hover:text-white transition"
                    >
                      {f.name}
                    </motion.button>
                  ))}

                  <a
                    href="https://wa.me/919999999999"
                    target="_blank"
                    className="col-span-2 mt-2 text-center px-3 py-2 rounded-lg
                               bg-green-600 text-white font-semibold"
                  >
                    Contact Sales
                  </a>
                </div>
              )}

              {/* FEATURE DETAILS */}
              {selected && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-3"
                >
                  <div className="p-3 border-l-4 border-blue-600 bg-blue-50 text-gray-900 rounded">
                    <strong>{selected.name}</strong>
                    <p className="mt-1">{selected.desc}</p>
                  </div>

                  <button
                    onClick={() => setSelected(null)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
                  >
                    More Options
                  </button>
                </motion.div>
              )}
            </div>

            {/* INPUT */}
            <div className="p-3 border-t flex gap-2">
              <input
                ref={inputRef}
                placeholder="Type a message..."
                className="flex-1 px-3 py-2 rounded-lg border outline-none text-sm"
              />
              <button
                onClick={sendMessage}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
              >
                Send
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Chatbot;
